<template>
  <v-card outlined class="elevation-0 ma-4">
    <div class="panel-header pa-4">
      <div class="mtm-header mb-2">
        <div class="panel-tools">
          <v-text-field
            v-model="positionQuery"
            :placeholder="$t('ndfPositions.searchContracts')"
            dense
            hide-details
            solo
            flat
            background-color="transparent"
            class="search-positions"
            prepend-inner-icon="mdi-magnify"
          />
        </div>

        <div class="mtm-controls">
          <button
            class="btn-pause"
            :class="{ 'is-resume': !isLive || !mockedEnabled }"
            @click="isLive = !isLive"
          >
            {{ mockedEnabled && isLive ? $t('portfolioPositions.pauseFeed') : $t('portfolioPositions.resumeFeed') }}
          </button>

          <v-chip small class="pill-live" :class="{ 'is-paused': !isLive || !mockedEnabled }">
            {{ mockedEnabled && isLive ? $t('portfolioPositions.live') : $t('portfolioPositions.paused') }}
          </v-chip>
        </div>
      </div>

      <!-- KPIs de um book de NDF: exposicao direcional, marcacao, movimento do
           dia e o que esta prestes a liquidar. -->
      <div class="mtm-metrics">
        <div class="metric-card">
          <div class="metric-label">{{ $t('ndfPositions.netNotional') }}</div>
          <div class="metric-value" :class="summary.netNotionalBRL >= 0 ? 'up' : 'down'">
            {{ money(summary.netNotionalBRL, true, true) }}
          </div>
          <div class="hint">{{ $t('ndfPositions.grossNotional', { value: money(summary.grossNotionalBRL, false, true) }) }}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">{{ $t('ndfPositions.mtmTotal') }}</div>
          <div class="metric-value" :class="summary.mtm >= 0 ? 'up' : 'down'">
            {{ money(summary.mtm, true, true) }}
          </div>
          <div class="hint">{{ $t('ndfPositions.mtmNominalNote') }}</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">{{ $t('ndfPositions.dayChange') }}</div>
          <div class="metric-value" :class="summary.dayChange >= 0 ? 'up' : 'down'">
            {{ money(summary.dayChange, true, true) }}
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-label">{{ $t('ndfPositions.maturingD7') }}</div>
          <div class="metric-value">{{ money(summary.maturingD7BRL, false, true) }}</div>
          <div class="hint">{{ $t('ndfPositions.fixingsToday', { count: summary.fixingsToday }) }}</div>
        </div>
      </div>
    </div>

    <div class="table-wrap">
      <base-data-table
        :headers="ndfHeaders"
        :items="filteredRows"
        :items-per-page="-1"
        :mobile-breakpoint="0"
        hide-default-footer
        dense
        class="table elevation-0 rt-table"
      >
        <!-- Par + ponta -->
        <template slot="item.pair" slot-scope="{ item }">
          <div class="symbol">{{ item.pair }}</div>
          <v-chip x-small class="side-chip" :class="item.side === 'BUY' ? 'side-buy' : 'side-sell'">
            {{ item.side === 'BUY' ? $t('ndfPositions.buy') : $t('ndfPositions.sell') }}
          </v-chip>
        </template>

        <!-- Vencimento -->
        <template slot="item.maturity" slot-scope="{ item }">
          <div class="td-right">{{ formatDate(item.maturity) }}</div>
          <div class="hint">
            {{ item.expired ? $t('ndfPositions.expired') : $t('ndfPositions.businessDays', { du: item.du, dc: item.dc }) }}
          </div>
          <div class="hint">{{ $t('ndfPositions.fixingOn', { date: formatDate(item.fixingDate) }) }}</div>
        </template>

        <!-- Nexus Price: o lado ja e o de FECHAMENTO da posicao -->
        <template slot="item.nexus" slot-scope="{ item }">
          <div class="mono strong td-right">{{ rate(item.nexus, item.pair) }}</div>
          <div class="hint">
            {{ item.side === 'BUY' ? $t('ndfPositions.onBid') : $t('ndfPositions.onAsk') }}
          </div>
          <div class="hint">
            {{ $t('portfolioPositions.bidPrefix') }} {{ rate(item.bid, item.pair) }} ·
            {{ $t('portfolioPositions.askPrefix') }} {{ rate(item.ask, item.pair) }}
          </div>
        </template>

        <!-- Taxa contratada -->
        <template slot="item.strike" slot-scope="{ item }">
          <div class="td-right mono">{{ rate(item.strike, item.pair) }}</div>
        </template>

        <!-- Diferenca contra a contratada, em pontos -->
        <template slot="item.points" slot-scope="{ item }">
          <div :class="['td-right', item.points >= 0 ? 'up' : 'down']">
            {{ item.points >= 0 ? '+' : '' }}{{ formatNumber(item.points, 0) }} {{ $t('ndfPositions.points') }}
          </div>
          <div class="hint">{{ pctVsStrike(item) }}</div>
        </template>

        <!-- Notional -->
        <template slot="item.notional" slot-scope="{ item }">
          <div class="td-right mono">{{ item.currency }} {{ formatNumber(item.notional, 0) }}</div>
        </template>

        <template slot="item.weight" slot-scope="{ item }">
          <div class="td-right">{{ item.weightPct.toFixed(2) }}%</div>
        </template>

        <template slot="item.notionalBRL" slot-scope="{ item }">
          <div class="td-right mono">{{ money(item.signedNotionalBRL, true, true) }}</div>
        </template>

        <!-- MTM -->
        <template slot="item.mtm" slot-scope="{ item }">
          <div :class="['strong', 'td-right', item.mtm >= 0 ? 'up' : 'down']">
            {{ money(item.mtm, true) }}
          </div>
          <div class="hint">{{ $t('ndfPositions.dayChangeShort') }} {{ money(item.dayChange, true) }}</div>
          <div class="hint">{{ item.counterparty }}</div>
        </template>
      </base-data-table>
    </div>

    <!-- Escada de vencimentos e exposicao por par: os dois paineis pelos quais
         um book de NDF e monitorado (o par de ETF era top/pior performer). -->
    <div class="mtm-summaries">
      <section class="summary-card">
        <header class="summary-header">
          <span class="bullet accent"></span>
          <h3>{{ $t('ndfPositions.maturityLadder') }}</h3>
        </header>
        <div v-for="b in ladder" :key="b.key" class="ladder-row">
          <div class="ladder-label">{{ $t('ndfPositions.buckets.' + b.key) }}</div>
          <div class="ladder-bar">
            <div
              class="ladder-fill"
              :class="b.net >= 0 ? 'fill-up' : 'fill-down'"
              :style="{ width: barWidth(b.gross, maxLadderGross) }"
            ></div>
          </div>
          <div class="ladder-value" :class="b.net >= 0 ? 'up' : 'down'">
            {{ money(b.net, true, true) }}
          </div>
        </div>
      </section>

      <section class="summary-card">
        <header class="summary-header">
          <span class="bullet accent"></span>
          <h3>{{ $t('ndfPositions.exposureByPair') }}</h3>
        </header>
        <div v-for="p in byPair" :key="p.pair" class="ladder-row">
          <div class="ladder-label">
            {{ p.pair }}
            <span class="hint">{{ $t('ndfPositions.contracts', { count: p.count }) }}</span>
          </div>
          <div class="ladder-bar">
            <div
              class="ladder-fill"
              :class="p.net >= 0 ? 'fill-up' : 'fill-down'"
              :style="{ width: barWidth(p.gross, maxPairGross) }"
            ></div>
          </div>
          <div class="ladder-value" :class="p.net >= 0 ? 'up' : 'down'">
            {{ money(p.net, true, true) }}
          </div>
        </div>
        <div v-if="!byPair.length" class="hint">{{ $t('ndfPositions.noContracts') }}</div>
      </section>
    </div>
  </v-card>
</template>

<script>
import { mapStores } from 'pinia'
import { usePortfolioStore } from '@/store/portfolioStore.js'
import { ladderByMaturity, exposureByPair, pairConfig } from '@/utils/ndfPortfolio'

export default {
  name: 'NdfPositions',
  props: { mockedEnabled: { type: Boolean } },
  data () {
    return {
      positionQuery: '',
      isLive: true,
      _feedTimer: null,
    }
  },

  computed: {
    ...mapStores(usePortfolioStore),

    ndfHeaders () {
      return [
        { text: this.$t('ndfPositions.headers.pairSide'),    value: 'pair',        sortable: false },
        { text: this.$t('ndfPositions.headers.maturity'),    value: 'maturity',    sortable: false, align: 'end' },
        { text: this.$t('ndfPositions.headers.nexusPrice'),  value: 'nexus',       sortable: false, align: 'end' },
        { text: this.$t('ndfPositions.headers.strike'),      value: 'strike',      sortable: false, align: 'end' },
        { text: this.$t('ndfPositions.headers.vsStrike'),    value: 'points',      sortable: false, align: 'end' },
        { text: this.$t('ndfPositions.headers.notional'),    value: 'notional',    sortable: false, align: 'end' },
        { text: this.$t('portfolioPositions.headers.weight'), value: 'weight',     sortable: false, align: 'end' },
        { text: this.$t('ndfPositions.headers.notionalBrl'), value: 'notionalBRL', sortable: false, align: 'end' },
        { text: this.$t('ndfPositions.headers.mtm'),         value: 'mtm',         sortable: false, align: 'end' },
      ]
    },

    // As linhas ja vem precificadas do store — o painel de propriedades da
    // direita le exatamente as mesmas.
    rows () {
      return this.portfolioStore.ndfRows
    },
    summary () {
      return this.portfolioStore.ndfSummary
    },
    currencyCode () {
      return (this.portfolioStore.selectedPortfolio && this.portfolioStore.selectedPortfolio.currency) || 'BRL'
    },
    filteredRows () {
      const q = (this.positionQuery || '').trim().toLowerCase()
      if (!q) return this.rows
      return this.rows.filter(r =>
        (r.pair && r.pair.toLowerCase().includes(q)) ||
        (r.counterparty && r.counterparty.toLowerCase().includes(q))
      )
    },
    ladder () {
      return ladderByMaturity(this.rows).filter(b => b.count > 0)
    },
    maxLadderGross () {
      return this.ladder.reduce((m, b) => Math.max(m, b.gross), 0)
    },
    byPair () {
      return exposureByPair(this.rows)
    },
    maxPairGross () {
      return this.byPair.reduce((m, p) => Math.max(m, p.gross), 0)
    },
  },

  watch: {
    isLive: 'syncFeed',
    mockedEnabled: 'syncFeed',
  },

  mounted () {
    this.portfolioStore.snapshotNdfOpen()
    this.syncFeed()
  },

  beforeDestroy () {
    this._stopFeed()
  },

  methods: {
    syncFeed () {
      if (this.mockedEnabled && this.isLive) this._startFeed()
      else this._stopFeed()
    },
    _startFeed () {
      this._stopFeed()
      this._feedTimer = setInterval(() => {
        this.portfolioStore.tickNdfCurves()
      }, 5000)
    },
    _stopFeed () {
      if (this._feedTimer) {
        clearInterval(this._feedTimer)
        this._feedTimer = null
      }
    },

    barWidth (value, max) {
      if (!max) return '0%'
      return `${Math.max(2, (Math.abs(value) / max) * 100)}%`
    },
    pctVsStrike (item) {
      if (!item.strike) return ''
      const pct = ((item.nexus - item.strike) / item.strike) * 100 * item.sign
      return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`
    },
    rate (value, pair) {
      return Number(value || 0).toFixed(pairConfig(pair).decimals)
    },
    formatDate (iso) {
      if (!iso) return '-'
      const [y, m, d] = String(iso).slice(0, 10).split('-')
      return `${d}/${m}/${y}`
    },
    formatNumber (n, digits = 2) {
      try {
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: digits,
          maximumFractionDigits: digits,
        }).format(Number(n) || 0)
      } catch (e) { return n }
    },
    money (value, withSign = true, compact = false) {
      const n = Number(value) || 0
      const ccy = this.currencyCode
      const sign = withSign ? (n > 0 ? '+' : n < 0 ? '-' : '') : (n < 0 ? '-' : '')
      const abs = Math.abs(n)
      if (compact) {
        if (abs >= 1e9) return `${sign}${ccy} ${(abs / 1e9).toFixed(2)}B`
        if (abs >= 1e6) return `${sign}${ccy} ${(abs / 1e6).toFixed(2)}M`
        if (abs >= 1e3) return `${sign}${ccy} ${(abs / 1e3).toFixed(2)}K`
      }
      return `${sign}${ccy} ${this.formatNumber(abs)}`
    },
  },
}
</script>

<style scoped>
/* As classes de layout (.mtm-metrics, .rt-table, .up/.down, .hint...) vem do
   <style> NAO scoped de PortfolioPositions.vue, que e global de proposito. Aqui
   ficam so as que sao exclusivas do NDF. */

.side-chip {
  font-weight: 700;
  margin-top: 4px;
}
.side-buy {
  background: rgba(45, 197, 22, 0.18) !important;
  border: 0.06rem solid var(--success) !important;
  color: var(--success) !important;
}
.side-sell {
  background: rgba(216, 11, 11, 0.18) !important;
  border: 0.06rem solid var(--danger) !important;
  color: var(--danger) !important;
}

.ladder-row {
  display: grid;
  grid-template-columns: 90px 1fr 130px;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.ladder-label {
  font-size: 12px;
  color: var(--text);
  font-weight: 700;
}
.ladder-label .hint {
  display: block;
  font-weight: 400;
}
.ladder-bar {
  background: #232730;
  border-radius: 999px;
  height: 10px;
  overflow: hidden;
}
.ladder-fill {
  height: 100%;
  border-radius: 999px;
}
.fill-up { background: var(--success); }
.fill-down { background: var(--danger); }
.ladder-value {
  font-size: 12px;
  font-weight: 800;
  text-align: right;
}
.bullet.accent { background: #ffbf00; }
</style>
